import { toAbsolutePublicUrl } from "../../../services/mediaLibraryServices";

export const FALLBACK_PUBLICATION_IMAGE = "/images/publication-placeholder.jpg";

export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

export function toBoolean(value) {
  return value === true || value === 1 || value === "1";
}

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", maxLength = 180) {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}…`;
}

export function normalizeSearchText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function parseDate(value) {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value) {
  const date = parseDate(value);
  if (!date) return "";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export function resolvePublicationImage(value) {
  if (!value) return FALLBACK_PUBLICATION_IMAGE;
  return toAbsolutePublicUrl(value) || FALLBACK_PUBLICATION_IMAGE;
}

export function resolvePublicationLink(value) {
  const link = String(value || "").trim();
  if (!link) return "";

  if (/^https?:\/\//i.test(link)) return link;

  return toAbsolutePublicUrl(link) || link;
}

export function getPublicationDate(publication) {
  return publication?.updated_at || publication?.created_at || "";
}

export function isPdfLink(value = "") {
  return /\.pdf(\?|#|$)/i.test(String(value || ""));
}

export function isExternalLink(value = "") {
  return /^https?:\/\//i.test(String(value || ""));
}

export function isActivePublication(publication) {
  return toBoolean(publication?.is_active);
}

export function normalizePublication(publication) {
  const title = publication?.titre || "Publication";
  const descriptionRaw = publication?.description || "";
  const descriptionText = stripHtml(descriptionRaw);

  const dateValue = getPublicationDate(publication);
  const dateLabel = formatDate(dateValue);

  const image = resolvePublicationImage(publication?.image_publication_url);
  const link = resolvePublicationLink(publication?.lien_publication_url);

  const normalized = {
    id: publication?.id,
    raw: publication,

    title,
    descriptionRaw,
    descriptionText,
    excerpt: truncateText(descriptionText, 190),

    dateValue,
    dateLabel,

    image,
    imageAlt: title,

    link,
    hasLink: Boolean(link),
    isPdf: isPdfLink(link),
    isExternal: isExternalLink(link),

    sortDate: parseDate(dateValue)?.getTime() || 0,
  };

  normalized.searchIndex = normalizeSearchText(
    [
      normalized.title,
      normalized.descriptionText,
      normalized.dateLabel,
      normalized.isPdf ? "pdf document" : "",
    ]
      .filter(Boolean)
      .join(" ")
  );

  return normalized;
}

export function sortPublications(items) {
  return [...items].sort((a, b) => {
    if (a.sortDate !== b.sortDate) {
      return b.sortDate - a.sortDate;
    }

    return normalizeSearchText(a.title).localeCompare(
      normalizeSearchText(b.title),
      "fr"
    );
  });
}